import React, { useState, useRef, useEffect, useCallback } from "react";
import {
  Box,
  Input,
  InputGroup,
  InputLeftElement,
  List,
  ListItem,
  Text,
  useOutsideClick,
} from "@chakra-ui/react";
import { Search } from "lucide-react";
import { useTranslation } from "react-i18next";
import L from "leaflet";
import { IStation } from "stores/data/oceanWeatherIntelligences/oceanWeatherIntelligenceModel";

interface StationSearchInputProps {
  stations: IStation[];
  onSelectStation: (station: IStation) => void;
  onSelectCoordinates: (lat: number, lon: number) => void;
  placeholder?: string;
}

interface SearchResult {
  type: "station" | "coordinates";
  station?: IStation;
  lat?: number;
  lon?: number;
  label: string;
  subtitle: string;
}

// Hook for debouncing
function useDebounce<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
}

// Parse coordinates from string
function parseCoordinates(input: string): { lat: number; lon: number } | null {
  const trimmed = input.trim();

  // Format: "-23.5505, -46.6333" or "-23.5505 -46.6333"
  const commaMatch = trimmed.match(/^(-?\d+\.?\d*)\s*,\s*(-?\d+\.?\d*)$/);
  if (commaMatch) {
    const lat = parseFloat(commaMatch[1]);
    const lon = parseFloat(commaMatch[2]);
    if (isValidCoordinate(lat, lon)) {
      return { lat, lon };
    }
  }

  // Format with space separator
  const spaceMatch = trimmed.match(/^(-?\d+\.?\d*)\s+(-?\d+\.?\d*)$/);
  if (spaceMatch) {
    const lat = parseFloat(spaceMatch[1]);
    const lon = parseFloat(spaceMatch[2]);
    if (isValidCoordinate(lat, lon)) {
      return { lat, lon };
    }
  }

  return null;
}

function isValidCoordinate(lat: number, lon: number): boolean {
  return lat >= -90 && lat <= 90 && lon >= -180 && lon <= 180;
}

export const StationSearchInput: React.FC<StationSearchInputProps> = ({
  stations,
  onSelectStation,
  onSelectCoordinates,
  placeholder,
}) => {
  const { t } = useTranslation("translation", {
    keyPrefix: "oceanWeatherIntelligence.insightAlert",
  });
  const [searchTerm, setSearchTerm] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const [results, setResults] = useState<SearchResult[]>([]);
  const containerRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLUListElement>(null);
  const noResultsRef = useRef<HTMLDivElement>(null);

  const debouncedSearchTerm = useDebounce(searchTerm, 300);

  // Disable map event propagation for dropdown elements
  useEffect(() => {
    if (dropdownRef.current) {
      L.DomEvent.disableClickPropagation(dropdownRef.current);
      L.DomEvent.disableScrollPropagation(dropdownRef.current);
    }
  }, [isOpen, results.length]);

  useEffect(() => {
    if (noResultsRef.current) {
      L.DomEvent.disableClickPropagation(noResultsRef.current);
      L.DomEvent.disableScrollPropagation(noResultsRef.current);
    }
  }, [isOpen, searchTerm, results.length]);

  useOutsideClick({
    ref: containerRef,
    handler: () => setIsOpen(false),
  });

  const performSearch = useCallback(
    (term: string) => {
      if (!term.trim()) {
        setResults([]);
        return;
      }

      const searchResults: SearchResult[] = [];

      // Check if input is coordinates
      const coords = parseCoordinates(term);
      if (coords) {
        searchResults.push({
          type: "coordinates",
          lat: coords.lat,
          lon: coords.lon,
          label: `${coords.lat.toFixed(4)}, ${coords.lon.toFixed(4)}`,
          subtitle: t("coordinates"),
        });
      }

      // Early return if no stations to search (only show coordinates result if valid)
      if (!stations || stations.length === 0) {
        setResults(searchResults);
        return;
      }

      // Search stations by name (case-insensitive)
      const termLower = term.toLowerCase();
      const matchingStations = stations
        .filter((station) =>
          station.station_name.toLowerCase().includes(termLower)
        )
        .slice(0, coords ? 9 : 10); // Leave room for coordinates result if present

      matchingStations.forEach((station) => {
        searchResults.push({
          type: "station",
          station,
          label: station.station_name,
          subtitle: `${station.station_lat.toFixed(
            4
          )}, ${station.station_lon.toFixed(4)}`,
        });
      });

      setResults(searchResults.slice(0, 10));
    },
    [stations, t]
  );

  useEffect(() => {
    performSearch(debouncedSearchTerm);
  }, [debouncedSearchTerm, performSearch]);

  // Find station by coordinates (with tolerance for floating point comparison)
  const findStationByCoordinates = useCallback(
    (lat: number, lon: number): IStation | null => {
      const tolerance = 0.0001; // ~11 meters tolerance
      return (
        stations.find(
          (station) =>
            Math.abs(station.station_lat - lat) < tolerance &&
            Math.abs(station.station_lon - lon) < tolerance
        ) || null
      );
    },
    [stations]
  );

  const handleSelect = (result: SearchResult) => {
    if (result.type === "station" && result.station) {
      onSelectStation(result.station);
    } else if (
      result.type === "coordinates" &&
      result.lat !== undefined &&
      result.lon !== undefined
    ) {
      // Check if there's a station at these coordinates
      const existingStation = findStationByCoordinates(result.lat, result.lon);
      if (existingStation) {
        onSelectStation(existingStation);
      } else {
        onSelectCoordinates(result.lat, result.lon);
      }
    }
    setSearchTerm("");
    setResults([]);
    setIsOpen(false);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
    setIsOpen(true);
  };

  const handleFocus = () => {
    if (searchTerm.trim()) {
      setIsOpen(true);
    }
  };

  return (
    <Box ref={containerRef} position="relative" width="100%">
      <InputGroup size="md" height="36px">
        <InputLeftElement pointerEvents="none" height="36px">
          <Search size={18} color="gray" />
        </InputLeftElement>
        <Input
          placeholder={
            placeholder || (t("searchStationOrCoordinates") as string)
          }
          value={searchTerm}
          onChange={handleInputChange}
          onFocus={handleFocus}
          bg="white"
          color="#57708A"
          borderRadius="md"
          height="36px"
          _placeholder={{ color: "#57708A", opacity: 0.7 }}
          _focus={{
            borderColor: "newBlue.500",
            boxShadow: "0 0 0 1px var(--chakra-colors-newBlue-500)",
          }}
        />
      </InputGroup>

      {isOpen && results.length > 0 && (
        <List
          ref={dropdownRef}
          position="absolute"
          top="100%"
          left={0}
          right={0}
          mt={1}
          bg="white"
          borderRadius="md"
          boxShadow="lg"
          border="1px solid"
          borderColor="gray.200"
          zIndex={1000}
          maxH="300px"
          overflowY="auto"
        >
          {results.map((result, index) => (
            <ListItem
              key={
                result.type === "station"
                  ? result.station?.station_id
                  : `coord-${index}`
              }
              px={3}
              py={2}
              cursor="pointer"
              _hover={{ bg: "gray.100" }}
              onClick={() => handleSelect(result)}
              borderBottom={index < results.length - 1 ? "1px solid" : "none"}
              borderColor="gray.100"
            >
              <Text fontSize="sm" fontWeight="medium" color="#57708A">
                {result.label}
              </Text>
              <Text fontSize="xs" color="#57708A" opacity={0.7}>
                {result.subtitle}
              </Text>
            </ListItem>
          ))}
        </List>
      )}

      {isOpen && searchTerm.trim() && results.length === 0 && (
        <Box
          ref={noResultsRef}
          position="absolute"
          top="100%"
          left={0}
          right={0}
          mt={1}
          bg="white"
          borderRadius="md"
          boxShadow="lg"
          border="1px solid"
          borderColor="gray.200"
          zIndex={1000}
          px={3}
          py={2}
        >
          <Text fontSize="sm" color="#57708A">
            {t("noResultsFound")}
          </Text>
        </Box>
      )}
    </Box>
  );
};

export default StationSearchInput;
